import { ref } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { buildTfDetailTotalsRow } from '@/views/cromis/reports/stock_production/stockProductionReportHelpers'

export default function useStockProductionList() {
    const toast = useToast()
    const records = ref([])
    const loading = ref(false)
    const totalRecords = ref(0)
    const meta = ref({})
    const tfDetailTotalsRow = ref(null)

    const formatNumber = value => {
        if (value === null || value === undefined || value === '') return ''
        const normalized = String(value).replace(/,/g, '')
        const number = Number(normalized)
        if (Number.isNaN(number)) return value
        let formatted = number.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
        })
        formatted = formatted.replace(/(\.[0-9]*[1-9])0+$/, '$1').replace(/\.0+$/, '')
        return formatted
    }

    const columns = [
        { key: 'warehouse_name', label: 'Warehouse', sortable: true },
        { key: 'tfNumber', label: 'TF Number', sortable: true },
        { key: 'Source_Destination', label: 'Source / Destination', sortable: true },
        { key: 'transaction_date', label: 'Transaction Date', sortable: true },
        {
            key: 'Bales_Received', label: 'Bales Received', sortable: true, formatter: value => formatNumber(value),
        },
        {
            key: 'Mass_Received', label: 'Mass Received', sortable: true, formatter: value => formatNumber(value),
        },
        {
            key: 'Bales_Dispatched', label: 'Bales Dispatched', sortable: true, formatter: value => formatNumber(value),
        },
        {
            key: 'Mass_Dispatched', label: 'Mass Dispatched', sortable: true, formatter: value => formatNumber(value),
        },
        {
            key: 'Weight_loss', label: 'Weight Loss', sortable: true, formatter: value => formatNumber(value),
        },
    ]

    const fetchReport = async params => {
        loading.value = true
        const reportType = params && params.reportType

        try {
            const response = await store.dispatch('cromis-stock-production/report', params)
            const payload = response.data
            const data = payload.warehouse_stock_movements || payload.data || payload.records || payload
            records.value = Array.isArray(data) ? data.map(item => ({
                ...item,
                tfNumber: item.tfNumber ?? item.system_tf_number ?? item.system_tf_no ?? item.systemTfNumber ?? item.tf_number ?? '',
                system_tf_number: item.system_tf_number ?? item.system_tf_no ?? item.systemTfNumber ?? item.tf_number ?? item.tfNumber ?? '',
                scanner_full_Name: item.scanner_full_Name ?? item.scanner_full_name ?? item.scannerFullName ?? '',
                loaded_at: item.loaded_at ?? item.loadedAt ?? '',
                MarkedAs: item.MarkedAs ?? item.marked_as ?? item.markedAs ?? '',
                barcode: item.barcode ?? item.Barcode ?? '',
                purchase_mass: item.purchase_mass ?? item.purchaseMass ?? item.purchaseweight ?? item.purchase_weight ?? '',
                Dispatched: item.Dispatched ?? item.dispatched ?? item.dispatch_mass ?? item.dispatchMass ?? '',
                received_by: item.received_by ?? item.receivedBy ?? '',
                received_at: item.received_at ?? item.receivedAt ?? '',
                Received: item.Received ?? item.received ?? item.received_mass ?? item.receivedMass ?? '',
                Source_Destination: item.Source_Destination || item.source_destination || item.sourceDestination || '',
                transaction_date: item.transaction_date || item.transactionDate || '',
                Bales_Received: item.Bales_Received || item.bales_received || item.balesReceived || '',
                Mass_Received: item.Mass_Received || item.mass_received || item.massReceived || '',
                Bales_Dispatched: item.Bales_Dispatched || item.bales_dispatched || item.balesDispatched || '',
                Mass_Dispatched: item.Mass_Dispatched || item.mass_dispatched || item.massDispatched || '',
                Weight_loss: item.Weight_loss || item.weight_loss || item.weightLoss || '',
            })) : []

            if (reportType === 'tf_detail_by_tf_number') {
                tfDetailTotalsRow.value = buildTfDetailTotalsRow(records.value)
            } else {
                tfDetailTotalsRow.value = null
            }

            if (payload.meta) {
                meta.value = payload.meta
                totalRecords.value = payload.meta.total || records.value.length
            } else {
                totalRecords.value = records.value.length
            }
        } catch (error) {
            toast({
                component: ToastificationContent,
                props: {
                    title: 'Error fetching report data',
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                },
            })
            tfDetailTotalsRow.value = null
        } finally {
            loading.value = false
        }
    }

    return {
        records,
        columns,
        loading,
        totalRecords,
        meta,
        tfDetailTotalsRow,
        fetchReport,
    }
}
